import { sum } from 'lodash';
import { CanvaSX, cloneNode, normalizeNode, renderNode } from '../canvasx';
import { defaultShapeProps, normalizeGap, normalizeSpacing } from '../shapes';
import {
  calculateDimensionsForNode,
  calculateDimensionsForElement,
  drawRect,
  drawSeparator,
  isElement,
  getFlexGrowValue,
} from '../util';
import { type FlexChild } from './FlexRow';

const defaultFlexColumnProps = {
  ...defaultShapeProps,
};

export const horizontalAlignments = [
  'left',
  'right',
  'center',
  'stretch',
] as const;
export type HorizontalAlignment = (typeof horizontalAlignments)[number];

export const verticalAlignments = ['top', 'bottom', 'center'] as const;
export type VerticalAlignment = (typeof verticalAlignments)[number];

// Height is optional, but width is required
export type FlexColumnProps = Partial<ShapeStruct> &
  FlexContainer & {
    gap?: SpacingAxisStruct | number;
    horizontalAlignment?: HorizontalAlignment;
    verticalAlignment?: VerticalAlignment;
    width?: number;
    minWidth?: number;
    setRenderedDimensions?: (dimensions: DimensionStruct) => void;

    // Internal, used to memoize layout
    _layout?: FlexColumnLayout;
  };

interface FlexColumnLayout {
  children: FlexChild[];
  height: number;
  width: number;
}

function applyHorizontalAlignment(
  width: number,
  child: FlexChild,
  horizontalAlignment: HorizontalAlignment
) {
  const { node } = child;

  // Skip non-elements
  if (!isElement(node)) {
    return;
  }

  const diff = width - child.width;

  switch (horizontalAlignment) {
    case 'right':
      node.props.x = diff;
      break;

    case 'center':
      node.props.x = diff / 2;
      break;

    case 'stretch':
      node.props.width = width;
      if (node.props._dimensions) {
        node.props._dimensions.width = width;
      }
      break;

    case 'left':
    default:
      // Do nothing, this is the default behaviour
      break;
  }
}

function applyFlexGrow(
  children: FlexChild[],
  height: number,
  gap: number,
  separator = 0,
  padding: SpacingSidesStruct
) {
  const availableHeight =
    height -
    sum(children.map(child => child.height)) -
    (children.length - 1) * gap -
    (children.length - 1) * separator -
    padding.top -
    padding.bottom;

  if (availableHeight <= 0) {
    return;
  }

  const flexGrowChildren = children.filter(
    child => isElement(child.node) && child.node.props.flexGrow
  );
  if (flexGrowChildren.length < 1) {
    return;
  }

  const totalFlexGrow = sum(
    flexGrowChildren.map(child => {
      if (isElement(child.node)) {
        return getFlexGrowValue(child.node.props.flexGrow);
      }
      return 0;
    })
  );

  flexGrowChildren.forEach(child => {
    if (isElement(child.node)) {
      const flexGrowValue = getFlexGrowValue(child.node.props.flexGrow);
      const extraHeight = (availableHeight * flexGrowValue) / totalFlexGrow;

      child.height += extraHeight;
      child.node.props.height ||= 0;
      child.node.props.height += extraHeight;

      // Recalculate the dimensions for the node
      calculateDimensionsForElement(child.node, true);
    }
  });
}

function generateLayout(
  props: ElementProps<FlexColumnProps>
): FlexColumnLayout {
  if (props._layout) {
    return props._layout;
  }

  let {
    children = [],
    gap,
    horizontalAlignment = 'left',
    minWidth,
    width,
    height,
    separator,
    verticalAlignment,
  } = props;

  children = Array.isArray(children) ? children : [children];
  children = children.filter(Boolean);

  const gapSpacing = normalizeGap(gap);
  const padding = normalizeSpacing(props.padding);
  let maxWidth =
    typeof width === 'number' ? width - padding.left - padding.right : Infinity;

  // When using stretch, we need to apply the width to each child so the child can grow appropriately.
  // If a fixed width is not provided, we need to calculate the width of the widest child first. But,
  // we need to do this on a clone of the child, since we need to calcualte the widths first, find the
  // widest child, then apply that width to each child before generating the final layout.
  if (horizontalAlignment === 'stretch') {
    // If a width is not specified, calculate the max width based on clones of the children
    if (typeof width !== 'number') {
      const clonedChildren = children.map(child => cloneNode(child, true));

      const childWidths = clonedChildren.map(
        child => calculateDimensionsForNode(child).width
      );

      width = Math.max(0, ...childWidths);
      maxWidth = width - padding.left - padding.right;
    }

    // Apply the width to each child
    children.forEach(child => {
      if (isElement(child)) {
        child.props.width = maxWidth;
      }
    });
  }

  // Calculate the dimensions of each child item
  const columnChildren: FlexChild[] = children.map(child => {
    let origHeight: number | undefined;
    if (isElement(child)) {
      origHeight = child.props.height;
    }

    let dimensions = calculateDimensionsForNode(child);

    // If the width of the FlexColumn is defined, and the child is wider,
    // apply the smaller width and remeasure. This allows the child to
    // wrap or resize if needed.
    if (
      isElement(child) &&
      typeof width === 'number' &&
      dimensions.width > maxWidth
    ) {
      const resetDimensions: Partial<DimensionStruct> = {
        width: width - padding.left - padding.right,
        height: origHeight,
      };

      Object.assign(child.props, resetDimensions);
      if (child.props._dimensions) {
        child.props._dimensions = undefined;
      }

      dimensions = calculateDimensionsForNode(child);
    }

    return {
      node: child,
      ...dimensions,
    };
  });

  // Adjust the column to the width of the widest child, if necessary
  width =
    typeof width === 'number'
      ? width
      : Math.max(...columnChildren.map(child => child.width), minWidth ?? 0) +
        padding.left +
        padding.right;

  // Apply horizontalAlignment
  const contentWidth = width - padding.left - padding.right;
  columnChildren.forEach(child => {
    applyHorizontalAlignment(contentWidth, child, horizontalAlignment);
  });

  const separatorHeight =
    (columnChildren.length - 1) * (separator?.lineWidth ?? 0);
  const gapHeight = (columnChildren.length - 1) * gapSpacing.vertical;

  height =
    typeof height === 'number'
      ? height
      : sum(columnChildren.map(child => child.height)) +
        gapHeight +
        separatorHeight +
        padding.top +
        padding.bottom;

  // Apply flex grow if a fixed height was provided
  if (props.height) {
    applyFlexGrow(
      columnChildren,
      props.height,
      gapSpacing.vertical,
      separator?.lineWidth ?? 0,
      padding
    );
  }

  // Apply vertical alignment if specified and there's a fixed height
  if (verticalAlignment && verticalAlignment !== 'top') {
    const totalContentHeight =
      sum(columnChildren.map(child => child.height)) +
      gapHeight +
      separatorHeight;
    const availableHeight = height - padding.top - padding.bottom;
    const extraSpace = Math.max(0, availableHeight - totalContentHeight);

    if (extraSpace > 0) {
      if (verticalAlignment === 'bottom') {
        // Move all children down
        columnChildren.forEach(child => {
          if (isElement(child.node)) {
            child.node.props.y = (child.node.props.y || 0) + extraSpace;
          }
        });
      } else if (verticalAlignment === 'center') {
        // Center all children
        columnChildren.forEach(child => {
          if (isElement(child.node)) {
            child.node.props.y = (child.node.props.y || 0) + extraSpace / 2;
          }
        });
      }
    }
  }

  props._layout = { height, width, children: columnChildren };

  return props._layout;
}

/**
 * The FlexColumn component will render child components in a flexbox-like fashion.
 * It will calculate the widths of all the children, and then wrap them into columns
 * as space allows.
 *
 * A `setRenderedDimensions` callback can be used to be notified of the final
 * dimensions of the rendered columns.
 */
export const FlexColumn: CanvaSXComponent<
  FlexColumnProps,
  typeof defaultFlexColumnProps
> & {
  calculateDimensions: (
    props: ElementProps<FlexColumnProps>
  ) => DimensionStruct;
  recalculateDimensions: (
    props: ElementProps<FlexColumnProps>
  ) => DimensionStruct;
} = function (props) {
  const { ctx } = CanvaSX.context;
  const { gap, setRenderedDimensions, separator, border } = props;

  const layout = generateLayout(props);
  const { width, height } = layout;

  const { y = 0 } = props;

  // Call setRenderedDimensions
  if (setRenderedDimensions != null) {
    setRenderedDimensions({
      width,
      height,
    });
  }

  const gapSpacing = normalizeGap(gap);

  drawRect(ctx, { ...props, y, width, height }, () => {
    ctx.save();

    layout.children.forEach(({ height: childHeight, node }, i) => {
      if (separator && i > 0) {
        drawSeparator({ ctx, width, height: childHeight, separator, border });
      }

      renderNode(node);

      ctx.translate(0, childHeight + gapSpacing.vertical);
    });

    ctx.restore();
  });

  return null;
};

FlexColumn.defaultProps = defaultFlexColumnProps;

FlexColumn.calculateDimensions = (props: ElementProps<FlexColumnProps>) => {
  // Normalize children into measurable nodes/elements
  props.children = normalizeNode(props.children as CanvaSXNode);

  const layout = generateLayout(props);

  const { width, height } = layout;

  props.width = width;
  props.height = height;

  return { width, height };
};

FlexColumn.recalculateDimensions = (props: ElementProps<FlexColumnProps>) => {
  // Reset the layout, then recalculate
  delete props._layout;

  return FlexColumn.calculateDimensions(props);
};
